import type {
  DecisionLedgerDecisionCadenceDto,
  DecisionLedgerDecisionTypeDto,
  DecisionLedgerScopeKindDto,
  DecisionLedgerVerificationMetricSensitivityDto,
} from './decision-ledger';
import type { GovernedCapabilityManifest } from './ask-helix/contracts';
import { validateGovernedCapabilityManifest } from './ask-helix/contracts';
import { validateApprovedFormulaRefs } from './decision-agent-formula-ref-validation';
import type { AskHelixNumericBindingScaleDto } from './ask-helix';
import {
  assertContextKind,
  assertSchemaVersion,
  assertSourceClass,
} from './contracts/assertions';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';

export type { DecisionAgentSourceClass } from './contracts/sourceClasses';

export type DecisionAgentModuleStatus =
  | 'demo'
  | 'shadow'
  | 'governed'
  | 'active'
  | 'retired';

export type DecisionAgentUseCaseReadinessStatus =
  | 'live'
  | 'partial'
  | 'roadmap'
  | 'blocked';

export type DecisionAgentUseCaseModuleRegistrationStatus =
  | 'missing'
  | DecisionAgentModuleStatus;

export type DecisionAgentDomain =
  | 'strategy'
  | 'finance'
  | 'people'
  | 'hr'
  | 'operations'
  | 'commercial'
  | 'technology'
  | 'benchmark'
  | 'risk'
  | 'legal'
  | 'supply_chain'
  | 'manufacturing'
  | 'custom';

export type DecisionAgentFreshnessCadence =
  | 'realtime'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'ad_hoc';

export type DecisionAgentRequiredFor =
  | 'planning'
  | 'monitoring'
  | 'scenario'
  | 'decision_pack'
  | 'activation'
  | 'ask_helix';

export type DecisionAgentMissingBehavior = 'block' | 'degrade' | 'ignore_for_preview';

export type DecisionAgentSignalValueType = 'number' | 'string' | 'boolean' | 'date' | 'enum';

export type DecisionAgentToolSideEffectClass =
  | 'read_only'
  | 'simulation'
  | 'draft_write'
  | 'governed_write'
  | 'activation'
  | 'external_side_effect';

export type DecisionAgentToolApprovalPolicy =
  | 'none'
  | 'human_required'
  | 'governance_required'
  | 'external_approval_required';

export type DecisionAgentToolSensitivity = 'public' | 'internal' | 'confidential' | 'restricted';

export type DecisionAgentToolAuditLevel = 'none' | 'metadata' | 'full';

export type DecisionAgentRollbackPosture =
  | 'not_applicable'
  | 'reversible'
  | 'compensating_action_required'
  | 'irreversible';

export type DecisionAgentNumericMetadataContract = {
  definition: string;
  unit: string;
  scope: string;
  timeframe: string;
  source: string;
  asOf: string;
  reconciliationRule: string;
  /**
   * #4008 EPG-1b — the governance lifecycle of the APPROVED artifact this
   * metric was computed from, exactly as that artifact recorded it.
   *
   * `asOf` above states WHEN the number is true; these two state WHO signed the
   * artifact off and WHEN. The egress display fence
   * (`numericBindingCanvasAdmission`) will not print a figure whose declared
   * source class asserts approval while its `approver` / `approvedAt` carry the
   * `ASK_HELIX_*_NOT_RECORDED` sentinels — a claim of approval with no approval
   * on file is the one shape that must always fail closed. A module that omits
   * these does not get a neutral result: its real, approved figures are
   * suppressed (the #3983 defect class, re-measured as EPG-1b).
   *
   * BOTH FIELDS ARE OPTIONAL AND MUST NEVER BE SYNTHESIZED. They are copied
   * from the artifact row's `approvedByUserId` / `approvedAt` and from nowhere
   * else. When the artifact genuinely records no approver, the contract records
   * none, the sentinel is stamped downstream, and the number stays honestly
   * suppressed. There is no default, no fallback and no placeholder.
   */
  approver?: string;
  approvedAt?: string;
};

export type DecisionAgentNumericMetricContract = DecisionAgentNumericMetadataContract & {
  metricKey: string;
  label: string;
  /** Declares the storage scale for percent-form numeric output. */
  scale?: AskHelixNumericBindingScaleDto;
  sensitivity?: DecisionLedgerVerificationMetricSensitivityDto;
};

export type DecisionFamilyContract = {
  familyId: string;
  title: string;
  executiveQuestion: string;
  decisionTypes: string[];
  defaultOwnerRole: string;
  escalationLogic?: string;
};

export type RequiredStateContract = {
  stateKey: string;
  domain: DecisionAgentDomain;
  freshnessCadence: DecisionAgentFreshnessCadence;
  requiredFor: DecisionAgentRequiredFor[];
  missingBehavior: DecisionAgentMissingBehavior;
  sourceClass?: DecisionAgentSourceClass;
  description?: string;
};

export type SignalSchemaContract = Partial<DecisionAgentNumericMetadataContract> & {
  signalKey: string;
  label: string;
  valueType: DecisionAgentSignalValueType;
  unit?: string;
  sourceClass: DecisionAgentSourceClass;
  required: boolean;
  freshnessCadence: DecisionAgentFreshnessCadence;
  allowedValues?: string[];
};

export type GovernedToolContract = {
  toolName: string;
  description: string;
  /**
   * Optional capability-intent bindings for the lifecycle tool that executes
   * them. The ids are validated against this module's governed capability so
   * a catalog intent cannot point at an invented or foreign operation.
   */
  intentIds?: string[];
  /**
   * #5552 (CTO round order @ 21b2dd5f03) — how a module's intent binder must treat this
   * tool's `intentIds`. REQUIRED, closed discriminant — no optional field, no default
   * branch, so a new tool cannot silently fall through to either binder behavior.
   * `declared_only` means the binder leaves this tool's own declared list untouched — the
   * correct choice for almost every tool. `auto_fill_servable_reads` opts a `read_state`
   * tool INTO the binder's generic "every anchor this module's governed read path can
   * serve" computation, overwriting whatever `intentIds` it declared. A typed, per-tool
   * marker rather than a name check, so a module may register more than one `read_state`
   * tool (a scoreboard reader plus a narrower policy/receipt reader) without the narrower
   * one being swept into the generic list.
   */
  intentBindingMode: 'auto_fill_servable_reads' | 'declared_only';
  runtimePurpose?:
    | 'read_state'
    | 'evaluate_monitoring'
    | 'run_scenarios'
    | 'build_decision_pack'
    | 'approve_activate';
  sideEffectClass: DecisionAgentToolSideEffectClass;
  /** #5631 retired gate — `'none'` is the lawful default; no runtime approval gate. */
  approvalPolicy: DecisionAgentToolApprovalPolicy;
  /** #5631 — `[]` is lawful least privilege, not a missing declaration. */
  requiredPermissions: string[];
  sensitivity: DecisionAgentToolSensitivity;
  auditLevel: DecisionAgentToolAuditLevel;
  rollbackPosture: DecisionAgentRollbackPosture;
  idempotencyKeyRequired: boolean;
  /** #5631 retired gate — absent ⇒ `false`, no runtime approval gate. */
  humanApprovalRequired?: boolean;
};

export type ScenarioContract = {
  scenarioId: string;
  label: string;
  objective: string;
  requiredSignals: string[];
  deterministicToolNames: string[];
  outputMetrics: string[];
  blockedWhenMissingSignals?: boolean;
};

export type ApprovedFormulaRefContract = {
  formulaRefId?: string;
  formulaId: string;
  formulaVersion: string;
  requiredFor: DecisionAgentRequiredFor[];
  outputMetrics: string[];
  evidenceRefs: string[];
};

export type FormulaGovernanceExemptionContract = {
  trackingId: string;
  reason: string;
  deletionCriteria: string;
  activationBlockedUntilResolved: true;
};

export type ProofPolicyContract = {
  policyId: string;
  requiredEvidenceRefs: string[];
  approvedFormulaRefs?: ApprovedFormulaRefContract[];
  formulaGovernanceExemption?: FormulaGovernanceExemptionContract;
  requireNumericMetadata: boolean;
  /** #5631 / Law 15 (2026-08-04) — render-side fail-closed retired; absent ⇒ show-with-badge. */
  failClosedWhenMissingEvidence?: boolean;
  prohibitLlmComputedBoardNumbers: boolean;
  /**
   * Posture for an answer whose required evidence is PARTIALLY satisfied — at
   * least one required key resolved, none conflicting. 'label' publishes the
   * computed answer with per-key pending disclosure and provenance badges;
   * 'block' withholds the whole answer as before. Absent means 'label'.
   *
   * This does not weaken `failClosedWhenMissingEvidence`: zero satisfied keys,
   * conflicting evidence, posture source classes, LLM-computed board numbers
   * and missing numeric metadata block regardless of this setting.
   */
  partialAnswerPosture?: 'block' | 'label';
};

/**
 * The single place the partial-answer default lives. A partial governed answer
 * with labels beats a refusal (emission-conversion Phase B), so absence of the
 * field means 'label'; a module opts back into whole-answer blocking by
 * declaring 'block' explicitly.
 */
export function resolvePartialAnswerPosture(
  proofPolicy: Pick<ProofPolicyContract, 'partialAnswerPosture'>
): 'block' | 'label' {
  return proofPolicy.partialAnswerPosture ?? 'label';
}

export type DecisionPackContract = {
  templateId: string;
  title: string;
  sections: string[];
  /**
   * A receipt-preserving composition can expose only immutable source packets
   * and deliberate gaps. It has no module-owned numeric metric and therefore
   * cannot declare an approved formula binding or a board-number claim.
   */
  sourcePacketOnly?: true;
  requiredMetrics: DecisionAgentNumericMetricContract[];
};

export type DecisionAgentContextBindingKind =
  | 'active_strategy'
  | 'strategy_objective'
  | 'initiative'
  | 'capability'
  | 'ioi_contract'
  | 'portfolio_artifact'
  | 'evidence_requirement'
  | 'client_profile'
  | 'audience_profile';

export type DecisionAgentContextBindingRequiredFor =
  | DecisionAgentRequiredFor
  | 'routing'
  | 'executive_synthesis';

export type DecisionAgentContextMissingBehavior =
  | 'block'
  | 'withhold_section'
  | 'module_not_applicable';

export type DecisionAgentContextBindingRequirement = {
  bindingKey: string;
  kind: DecisionAgentContextBindingKind;
  requiredFor: DecisionAgentContextBindingRequiredFor[];
  missingBehavior: DecisionAgentContextMissingBehavior;
  description: string;
};

export type DecisionAgentModuleApplicabilityContract = {
  allowedScopeKinds: DecisionLedgerScopeKindDto[];
  requiredBindings: DecisionAgentContextBindingRequirement[];
  requiredEvidenceKeys: string[];
  optionalEvidenceKeys?: string[];
  moduleNotApplicableWhenMissingRequiredBindings: boolean;
};

export type DecisionAgentExecutiveOutputField =
  | 'headline'
  | 'executive_takeaway'
  | 'business_implication'
  | 'recommendation'
  | 'why_this_matters'
  | 'options_tradeoffs'
  | 'evidence_assumptions'
  | 'control_plan'
  | 'escalation_path';

export type DecisionAgentExecutiveSourceDetailMode =
  | 'drill_down_by_default'
  | 'top_level_when_recommendation_changes';

export type DecisionAgentExecutiveSynthesisContract = {
  leadWith: 'business_implication_and_recommendation';
  requiredFields: DecisionAgentExecutiveOutputField[];
  sourceDetailMode: DecisionAgentExecutiveSourceDetailMode;
  roleTailoringAllowed: 'emphasis_only_truth_unchanged';
  clientLanguageSource: 'client_profile';
  forbiddenVisibleTerms: string[];
};

export type DecisionAgentModuleOperatingMode =
  | 'standalone_and_composite'
  | 'standalone_only'
  | 'composite_only';

export type DecisionAgentModuleCapabilityProfile = {
  capabilityId: string;
  purpose: string;
  description: string;
  operatingMode: DecisionAgentModuleOperatingMode;
  canAnswer: string[];
  cannotAnswer: string[];
  /** #5631 — planner-facing docs only, never a routing input; `[]` is lawful. */
  exampleQuestions: string[];
  answerOutputs: string[];
  evidenceNeeds: string[];
  followUpGates: string[];
};

export type DecisionAgentAskHelixBridgeScenarioExecutionPolicy =
  | { mode: 'always' }
  | { mode: 'never' }
  | { mode: 'intent_driven'; intentIds: string[] };

export type DecisionAgentAskHelixBridgeScenarioOptInPolicy = {
  requiredForIntentIds: string[];
  questionTerms?: string[];
  explicitOptInArgument: string;
  message: string;
  missingKeys: string[];
};

export type DecisionAgentAskHelixBridgeDirectMetricAnswerPolicy = {
  intentIds?: string[];
  questionTerms?: string[];
  metricKeys: string[];
  evidenceStatement?: string;
};

export type DecisionAgentAskHelixBridgePolicy = {
  scenarioExecution?: DecisionAgentAskHelixBridgeScenarioExecutionPolicy;
  scenarioOptIn?: DecisionAgentAskHelixBridgeScenarioOptInPolicy;
  directMetricAnswer?: DecisionAgentAskHelixBridgeDirectMetricAnswerPolicy;
};

export type DecisionAgentContextPackScope = {
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
};

export type DecisionAgentContextPack = {
  schemaVersion: 'decision_agent_context_pack_v1';
  tenantId: string;
  actorUserId: string;
  moduleId?: string | null;
  decisionFamilyId?: string | null;
  audienceRole?: string | null;
  activeStrategyId?: string | null;
  strategyVersion?: string | null;
  scope: DecisionAgentContextPackScope;
  objectiveIds: string[];
  initiativeIds: string[];
  capabilityIds: string[];
  ioiContractRefs: string[];
  portfolioArtifactRefs: string[];
  evidenceRequirementRefs: string[];
  governedEvidenceKeys: string[];
  clientProfileRef?: string | null;
  permissions: string[];
  unknowns: string[];
  blockedReasons: string[];
};

export const DEFAULT_DECISION_AGENT_EXECUTIVE_FORBIDDEN_VISIBLE_TERMS = [
  'runtime',
  'DTO',
  'schema',
  'endpoint',
  'source refs',
  'evidence checking',
  'blocked reason',
  'tenant',
  'synthetic',
  'fixture',
  'validation',
  'provider',
  'config',
  'manifest',
  'workspace session',
  'action gate',
] as const;

export type LedgerBindingContract = {
  decisionType: DecisionLedgerDecisionTypeDto;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  ownerRole: string;
  cadence: DecisionLedgerDecisionCadenceDto;
  /** #5631 retired gate, RIF/WR no-approvals law — `false` is the lawful default. */
  activationRequiresApproval: boolean;
  verificationMetricKeys: string[];
};

export type MonitoringContract = {
  cadence: DecisionAgentFreshnessCadence;
  ruleIds: string[];
  outputMetrics?: string[];
  warningLevels: string[];
  failClosedWhenSignalsMissing: boolean;
};

/** #5631 — retired lifecycle-promotion gates; `[]`/`false` are lawful, unchecked values. */
export type EvalSuiteContract = {
  evalIds: string[];
  goldenTraceIds: string[];
  negativeEvalIds: string[];
  promotionGateIds: string[];
  requiredBeforeActivation: boolean;
};

export type DecisionAgentModuleManifest = {
  schemaVersion: 'decision_agent_module_manifest_v1';
  moduleId: string;
  displayName: string;
  version: string;
  status: DecisionAgentModuleStatus;
  /**
   * Ask Helix answering-surface certification flag (Module Factory law).
   *
   * When `false`, the module stays fully registered — its code, lifecycle,
   * admin catalog entry, and cross-module de-confliction all remain intact —
   * but it is WITHHELD from the Ask Helix answering roster so a live question
   * is never routed to it. This is how an uncertified clone/shell or a
   * fig-leaf lever that is deferred to "playbook-only / named, no dollars"
   * stops being offered without deleting its module.
   *
   * Absent (the default) or `true` means the module is offerable. Use
   * `isOfferableDecisionAgentModule` to read it.
   */
  offerable?: boolean;
  sourceClass: DecisionAgentSourceClass;
  syntheticDataLabel?: string;
  capabilityProfile: DecisionAgentModuleCapabilityProfile;
  governedCapability?: GovernedCapabilityManifest;
  askHelixBridge?: DecisionAgentAskHelixBridgePolicy;
  decisionFamilies: DecisionFamilyContract[];
  requiredState: RequiredStateContract[];
  signals: SignalSchemaContract[];
  governedTools: GovernedToolContract[];
  scenarios: ScenarioContract[];
  proofPolicy: ProofPolicyContract;
  decisionPack: DecisionPackContract;
  applicability?: DecisionAgentModuleApplicabilityContract;
  executiveSynthesis?: DecisionAgentExecutiveSynthesisContract;
  ledgerBinding: LedgerBindingContract;
  monitoring: MonitoringContract;
  evalSuite: EvalSuiteContract;
};

/**
 * True when a module should appear on the Ask Helix answering surface.
 *
 * A module is offerable unless it explicitly sets `offerable: false`. Uncertified
 * clones/shells and deferred fig-leaf levers set the flag off so routing never
 * hands them a live question, while the module stays registered for lifecycle,
 * admin catalog, and de-confliction.
 */
export function isOfferableDecisionAgentModule(
  manifest: Pick<DecisionAgentModuleManifest, 'offerable'>,
): boolean {
  return manifest.offerable !== false;
}

export type DecisionAgentUseCaseReadinessEntry = {
  useCaseId: string;
  title: string;
  executiveQuestion: string;
  persona: string;
  domains: DecisionAgentDomain[];
  readiness: DecisionAgentUseCaseReadinessStatus;
  moduleId: string;
  moduleDisplayName: string;
  moduleRegistrationStatus: DecisionAgentUseCaseModuleRegistrationStatus;
  sourceClass?: DecisionAgentSourceClass | null;
  requiredState: string[];
  deterministicTools: string[];
  requiredPermissions: string[];
  evalIds: string[];
  proofPolicy: {
    requiredEvidenceRefs: string[];
    requireNumericMetadata: boolean;
    failClosedWhenMissingEvidence: boolean;
    prohibitLlmComputedBoardNumbers: boolean;
  };
  decisionPackTemplate: string;
  ledgerDecisionType: string;
  installWorkflow: string;
  matchPhrases: string[];
  blockedReason?: string | null;
};

export type DecisionAgentModuleManifestValidationResult = {
  valid: boolean;
  errors: string[];
};

export class DecisionAgentModuleManifestValidationError extends Error {
  readonly errors: string[];

  constructor(errors: string[]) {
    super(`Invalid decision-agent module manifest: ${errors.join('; ')}`);
    this.name = 'DecisionAgentModuleManifestValidationError';
    this.errors = errors;
  }
}

export function validateDecisionAgentModuleManifest(
  manifest: DecisionAgentModuleManifest
): DecisionAgentModuleManifestValidationResult {
  const errors: string[] = [];
  assertManifestContracts(manifest, errors);
  validateManifestStructure(manifest, errors);
  validateManifestSections(manifest, errors);
  return {
    valid: errors.length === 0,
    errors,
  };
}

// #5631 r2 split — schema/source-class/context-kind contract assertions.
function assertManifestContracts(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  const ctx = { moduleId: manifest.moduleId, caller: 'validateDecisionAgentModuleManifest' };
  captureContractAssertion(() => assertSchemaVersion(manifest.schemaVersion, ctx), errors);
  captureContractAssertion(() => assertSourceClass(manifest.sourceClass, ctx), errors);
  const withContextKind = manifest as DecisionAgentModuleManifest & { contextKind?: unknown };
  if (Object.prototype.hasOwnProperty.call(withContextKind, 'contextKind')) {
    captureContractAssertion(() => assertContextKind(withContextKind.contextKind, ctx), errors);
  }
}

// #5631 r2 split — scalar-field and governed-collection presence requirements.
function validateManifestStructure(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  requireNonBlank(manifest.schemaVersion, 'schemaVersion', errors);
  if (manifest.schemaVersion !== 'decision_agent_module_manifest_v1') {
    errors.push('schemaVersion must be decision_agent_module_manifest_v1.');
  }
  requireNonBlank(manifest.moduleId, 'moduleId', errors);
  requireNonBlank(manifest.displayName, 'displayName', errors);
  requireNonBlank(manifest.version, 'version', errors);
  validateStatus(manifest.status, errors);

  if (manifest.sourceClass === 'synthetic-demo' && isBlank(manifest.syntheticDataLabel)) {
    errors.push('synthetic-demo modules must include syntheticDataLabel.');
  }

  validateCapabilityProfile(manifest.capabilityProfile, errors);

  if (!hasItems(manifest.decisionFamilies)) {
    errors.push('decisionFamilies must include at least one governed decision family.');
  }
  if (!hasItems(manifest.requiredState)) {
    errors.push('requiredState must include at least one governed state requirement.');
  }
  if (!hasItems(manifest.signals)) {
    errors.push('signals must include at least one signal schema.');
  }
  if (!hasItems(manifest.governedTools)) {
    errors.push('governedTools must include at least one governed tool contract.');
  }
  if (!hasItems(manifest.scenarios)) {
    errors.push('scenarios must include at least one deterministic scenario contract.');
  }
}

// #5631 r2 split — per-section validators, incl. governedCapability's own contract (optional).
function validateManifestSections(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  validateRequiredState(manifest.requiredState, manifest.moduleId, errors);
  validateSignals(manifest.signals, manifest.moduleId, errors);
  validateTools(manifest, errors);
  validateScenarios(manifest, errors);
  validateDecisionPack(manifest.decisionPack, errors);
  validateProofPolicy(manifest, errors);
  validateApplicability(manifest.applicability, errors);
  validateExecutiveSynthesis(manifest.executiveSynthesis, errors);
  validateAskHelixBridgePolicy(manifest, errors);
  validateLedgerBinding(manifest, errors);
  validateMonitoring(manifest, errors);
  if (manifest.governedCapability) {
    errors.push(...validateGovernedCapabilityManifest(manifest.governedCapability).errors);
  }
  // #5631 — evalSuite's fields are all retired gates; nothing left to validate.
}

function validateCapabilityProfile(
  capabilityProfile: DecisionAgentModuleCapabilityProfile | undefined,
  errors: string[]
): void {
  if (!capabilityProfile) {
    errors.push('capabilityProfile is required.');
    return;
  }

  requireNonBlank(capabilityProfile.capabilityId, 'capabilityProfile.capabilityId', errors);
  requireNonBlank(capabilityProfile.purpose, 'capabilityProfile.purpose', errors);
  requireNonBlank(capabilityProfile.description, 'capabilityProfile.description', errors);
  if (
    capabilityProfile.operatingMode !== 'standalone_and_composite' &&
    capabilityProfile.operatingMode !== 'standalone_only' &&
    capabilityProfile.operatingMode !== 'composite_only'
  ) {
    errors.push(
      'capabilityProfile.operatingMode must be standalone_and_composite, standalone_only, or composite_only.'
    );
  }

  requireStringArray(capabilityProfile.canAnswer, 'capabilityProfile.canAnswer', errors, 3);
  requireStringArray(
    capabilityProfile.cannotAnswer,
    'capabilityProfile.cannotAnswer',
    errors,
    1
  );
  // #5631 — retired gate: [] is a lawful "no curated examples" declaration.
  requireStringArray(
    capabilityProfile.exampleQuestions,
    'capabilityProfile.exampleQuestions',
    errors,
    0
  );
  requireStringArray(
    capabilityProfile.answerOutputs,
    'capabilityProfile.answerOutputs',
    errors,
    1
  );
  requireStringArray(
    capabilityProfile.evidenceNeeds,
    'capabilityProfile.evidenceNeeds',
    errors,
    1
  );
  requireStringArray(
    capabilityProfile.followUpGates,
    'capabilityProfile.followUpGates',
    errors,
    1
  );
}

export function assertValidDecisionAgentModuleManifest(
  manifest: DecisionAgentModuleManifest
): asserts manifest is DecisionAgentModuleManifest {
  const result = validateDecisionAgentModuleManifest(manifest);
  if (!result.valid) {
    throw new DecisionAgentModuleManifestValidationError(result.errors);
  }
}

function validateRequiredState(
  requiredState: readonly RequiredStateContract[],
  moduleId: string,
  errors: string[]
): void {
  requiredState.forEach((state, index) => {
    if (Object.prototype.hasOwnProperty.call(state, 'sourceClass')) {
      captureContractAssertion(
        () =>
          assertSourceClass(state.sourceClass, {
            moduleId,
            caller: 'validateRequiredState',
          }),
        errors
      );
    }
    requireNonBlank(state.stateKey, `requiredState[${index}].stateKey`, errors);
    if (!hasItems(state.requiredFor)) {
      errors.push(
        `requiredState[${index}].requiredFor must include at least one runtime dependency.`
      );
    }
    if (
      state.requiredFor.some(
        (requiredFor) => requiredFor === 'scenario' || requiredFor === 'decision_pack'
      ) &&
      state.missingBehavior !== 'block'
    ) {
      errors.push(
        `requiredState[${index}] used for scenario or decision_pack must fail closed with missingBehavior block.`
      );
    }
  });
}

function validateSignals(
  signals: readonly SignalSchemaContract[],
  moduleId: string,
  errors: string[]
): void {
  const signalKeys = new Set<string>();
  signals.forEach((signal, index) => {
    captureContractAssertion(
      () =>
        assertSourceClass(signal.sourceClass, {
          moduleId,
          caller: 'validateSignals',
        }),
      errors
    );
    requireNonBlank(signal.signalKey, `signals[${index}].signalKey`, errors);
    requireNonBlank(signal.label, `signals[${index}].label`, errors);
    if (signalKeys.has(signal.signalKey)) {
      errors.push(`signals[${index}].signalKey duplicates an earlier signal.`);
    }
    signalKeys.add(signal.signalKey);
    if (signal.valueType === 'number' && !hasBoardGradeNumericMetadata(signal)) {
      errors.push(
        `signals[${index}] must include definition, unit, scope, timeframe, source, asOf, and reconciliationRule for numeric values.`
      );
    }
    if (signal.sourceClass === 'synthetic-demo' && !signal.source?.includes('synthetic-demo')) {
      errors.push(`signals[${index}] synthetic-demo source must be explicitly labeled.`);
    }
  });
}

function validateTools(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  const tools = manifest.governedTools;
  const toolNames = new Set<string>();
  const declaredIntentIds = new Set(
    manifest.governedCapability?.intents.map((intent) => intent.intentId) ?? [],
  );
  tools.forEach((tool, index) => {
    requireNonBlank(tool.toolName, `governedTools[${index}].toolName`, errors);
    requireNonBlank(tool.description, `governedTools[${index}].description`, errors);
    if (toolNames.has(tool.toolName)) {
      errors.push(`governedTools[${index}].toolName duplicates an earlier tool.`);
    }
    toolNames.add(tool.toolName);
    if (tool.intentIds) {
      requireStringArray(tool.intentIds, `governedTools[${index}].intentIds`, errors, 1);
      const boundIntentIds = new Set<string>();
      tool.intentIds.forEach((intentId, intentIndex) => {
        if (boundIntentIds.has(intentId)) {
          errors.push(
            `governedTools[${index}].intentIds[${intentIndex}] duplicates an earlier intent binding.`,
          );
        }
        boundIntentIds.add(intentId);
        if (!declaredIntentIds.has(intentId)) {
          errors.push(
            `governedTools[${index}].intentIds[${intentIndex}] must reference an intent declared by governedCapability.`,
          );
        }
      });
    }
    // #5631 — requiredPermissions: [] is lawful least privilege (retired gate).
    validateToolSideEffectContract(tool, index, errors);
  });
  validateIntentToolNameOwnership(manifest, errors);
}

/**
 * Baseline defect #2 — pins that a capability's own catalog projection can
 * never again advertise ONE flat name for several distinct governedTools[]
 * entries. Runs only when an intent opts in with `toolName`
 * (`GovernedCapabilityIntent.toolName`): the declared value must name a real
 * `governedTools[]` entry, and that entry's `intentIds` (when declared) must
 * actually own this intent. A capability whose intents never declare
 * `toolName` is unaffected — the check is additive, not retroactive.
 */
function validateIntentToolNameOwnership(
  manifest: DecisionAgentModuleManifest,
  errors: string[]
): void {
  const intents = manifest.governedCapability?.intents ?? [];
  intents.forEach((intent, index) => {
    if (typeof intent.toolName !== 'string' || intent.toolName.trim().length === 0) {
      return;
    }
    const owner = manifest.governedTools.find((tool) => tool.toolName === intent.toolName);
    if (!owner) {
      errors.push(
        `governedCapability.intents[${index}].toolName does not reference a declared governedTools[] entry.`
      );
      return;
    }
    if (owner.intentIds && !owner.intentIds.includes(intent.intentId)) {
      errors.push(
        `governedCapability.intents[${index}].toolName names a governedTools[] entry that does not own intent ${intent.intentId}.`
      );
    }
  });
}

// Write-side safety, not a retired boot-time gate: activation/governed_write/
// draft_write tools still name their own approval + audit posture explicitly.
function validateToolSideEffectContract(
  tool: GovernedToolContract,
  index: number,
  errors: string[]
): void {
  if (
    tool.sideEffectClass === 'activation' &&
    (tool.approvalPolicy !== 'governance_required' || !tool.humanApprovalRequired)
  ) {
    errors.push(
      `governedTools[${index}] sideEffectClass activation requires governance_required approval and human approval.`
    );
  }
  if (
    tool.sideEffectClass === 'activation' &&
    (!tool.idempotencyKeyRequired ||
      tool.auditLevel !== 'full' ||
      tool.rollbackPosture !== 'compensating_action_required')
  ) {
    errors.push(
      `governedTools[${index}] sideEffectClass activation requires full audit, idempotency, and compensating rollback posture.`
    );
  }
  if (
    tool.sideEffectClass === 'governed_write' &&
    (tool.approvalPolicy === 'none' || !tool.humanApprovalRequired)
  ) {
    errors.push(`governedTools[${index}] governed_write tools require explicit human approval.`);
  }
  if (
    tool.sideEffectClass === 'draft_write' &&
    (tool.approvalPolicy !== 'human_required' || !tool.humanApprovalRequired)
  ) {
    errors.push(`governedTools[${index}] draft_write tools require human_required approval.`);
  }
}

function validateScenarios(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  const signalKeys = new Set(manifest.signals.map((signal) => signal.signalKey));
  const toolNames = new Set(manifest.governedTools.map((tool) => tool.toolName));
  const metricKeys = new Set(
    manifest.decisionPack.requiredMetrics.map((metric) => metric.metricKey)
  );
  const scenarioIds = new Set<string>();

  manifest.scenarios.forEach((scenario, index) => {
    requireNonBlank(scenario.scenarioId, `scenarios[${index}].scenarioId`, errors);
    if (scenarioIds.has(scenario.scenarioId)) {
      errors.push(`scenarios[${index}].scenarioId duplicates an earlier scenario.`);
    }
    scenarioIds.add(scenario.scenarioId);
    if (!hasItems(scenario.requiredSignals)) {
      errors.push(`scenarios[${index}].requiredSignals must include at least one signal key.`);
    }
    scenario.requiredSignals.forEach((signalKey) => {
      if (!signalKeys.has(signalKey)) {
        errors.push(`scenarios[${index}].requiredSignals references unknown signal ${signalKey}.`);
      }
    });
    scenario.deterministicToolNames.forEach((toolName) => {
      if (!toolNames.has(toolName)) {
        errors.push(
          `scenarios[${index}].deterministicToolNames references unknown tool ${toolName}.`
        );
      }
    });
    scenario.outputMetrics.forEach((metricKey) => {
      if (!metricKeys.has(metricKey)) {
        errors.push(
          `scenarios[${index}].outputMetrics references unknown Decision Pack metric ${metricKey}.`
        );
      }
    });
  });
}

function validateProofPolicy(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  const proofPolicy = manifest.proofPolicy;
  requireNonBlank(proofPolicy.policyId, 'proofPolicy.policyId', errors);
  if (!hasItems(proofPolicy.requiredEvidenceRefs)) {
    errors.push('proofPolicy.requiredEvidenceRefs must include at least one evidence class.');
  }
  if (!proofPolicy.requireNumericMetadata) {
    errors.push('proofPolicy.requireNumericMetadata must be true.');
  }
  // #5631 / Law 15 — failClosedWhenMissingEvidence retired; absence is not a defect.
  if (!proofPolicy.prohibitLlmComputedBoardNumbers) {
    errors.push('proofPolicy.prohibitLlmComputedBoardNumbers must be true.');
  }
  const posture = proofPolicy.partialAnswerPosture;
  if (posture !== undefined && posture !== 'block' && posture !== 'label') {
    errors.push("proofPolicy.partialAnswerPosture must be 'block' or 'label' when set.");
  }
  validateApprovedFormulaRefs({
    decisionPack: manifest.decisionPack,
    errors,
    governedNumericBindings: manifest.governedCapability?.numericBindingsProduced ?? [],
    moduleId: manifest.moduleId,
    monitoring: manifest.monitoring,
    proofPolicy,
    requiredState: manifest.requiredState,
    scenarios: manifest.scenarios,
    validateRequiredFor: validateRequiredForValues,
  });
}

function validateRequiredForValues(
  values: readonly DecisionAgentRequiredFor[],
  label: string,
  errors: string[]
): void {
  const allowed: DecisionAgentRequiredFor[] = [
    'planning',
    'monitoring',
    'scenario',
    'decision_pack',
    'activation',
    'ask_helix',
  ];
  values.forEach((value) => {
    if (!allowed.includes(value)) {
      errors.push(`${label}.requiredFor must only include ${allowed.join(', ')}.`);
    }
  });
}

function validateDecisionPack(decisionPack: DecisionPackContract, errors: string[]): void {
  requireNonBlank(decisionPack.templateId, 'decisionPack.templateId', errors);
  requireNonBlank(decisionPack.title, 'decisionPack.title', errors);
  if (!hasItems(decisionPack.sections)) {
    errors.push('decisionPack.sections must include at least one section.');
  }
  if (!decisionPack.sourcePacketOnly && !hasItems(decisionPack.requiredMetrics)) {
    errors.push('decisionPack.requiredMetrics must include at least one metric.');
  }
  if (decisionPack.sourcePacketOnly && decisionPack.requiredMetrics.length > 0) {
    errors.push(
      'decisionPack.sourcePacketOnly must not declare module-owned numeric metrics.'
    );
  }
  const metricKeys = new Set<string>();
  decisionPack.requiredMetrics.forEach((metric, index) => {
    requireNonBlank(metric.metricKey, `decisionPack.requiredMetrics[${index}].metricKey`, errors);
    if (metricKeys.has(metric.metricKey)) {
      errors.push(`decisionPack.requiredMetrics[${index}].metricKey duplicates an earlier metric.`);
    }
    metricKeys.add(metric.metricKey);
    if (!hasBoardGradeNumericMetadata(metric)) {
      errors.push(
        `decisionPack.requiredMetrics[${index}] must include definition, unit, scope, timeframe, source, asOf, and reconciliationRule.`
      );
    }
  });
}

function validateApplicability(
  applicability: DecisionAgentModuleApplicabilityContract | undefined,
  errors: string[]
): void {
  if (!applicability) return;

  if (!hasItems(applicability.allowedScopeKinds)) {
    errors.push('applicability.allowedScopeKinds must include at least one governed scope kind.');
  }
  if (!hasItems(applicability.requiredBindings)) {
    errors.push('applicability.requiredBindings must include at least one context binding.');
  }
  if (!hasItems(applicability.requiredEvidenceKeys)) {
    errors.push('applicability.requiredEvidenceKeys must include at least one governed evidence key.');
  }
  if (!applicability.moduleNotApplicableWhenMissingRequiredBindings) {
    errors.push(
      'applicability.moduleNotApplicableWhenMissingRequiredBindings must be true so routing fails closed.'
    );
  }

  const bindingKeys = new Set<string>();
  let hasActiveStrategyBinding = false;
  applicability.requiredBindings?.forEach((binding, index) => {
    requireNonBlank(binding.bindingKey, `applicability.requiredBindings[${index}].bindingKey`, errors);
    requireNonBlank(binding.description, `applicability.requiredBindings[${index}].description`, errors);
    if (bindingKeys.has(binding.bindingKey)) {
      errors.push(
        `applicability.requiredBindings[${index}].bindingKey duplicates an earlier binding.`
      );
    }
    bindingKeys.add(binding.bindingKey);
    if (binding.kind === 'active_strategy') hasActiveStrategyBinding = true;
    if (!hasItems(binding.requiredFor)) {
      errors.push(
        `applicability.requiredBindings[${index}].requiredFor must include at least one runtime dependency.`
      );
    }
    if (
      binding.requiredFor.some(
        (requiredFor) =>
          requiredFor === 'routing' ||
          requiredFor === 'scenario' ||
          requiredFor === 'decision_pack' ||
          requiredFor === 'activation'
      ) &&
      binding.missingBehavior !== 'block' &&
      binding.missingBehavior !== 'module_not_applicable'
    ) {
      errors.push(
        `applicability.requiredBindings[${index}] used for routing, scenario, decision_pack, or activation must fail closed.`
      );
    }
  });

  if (!hasActiveStrategyBinding) {
    errors.push(
      'applicability.requiredBindings must include active_strategy so modules route from governed strategy context.'
    );
  }
}

function validateExecutiveSynthesis(
  executiveSynthesis: DecisionAgentExecutiveSynthesisContract | undefined,
  errors: string[]
): void {
  if (!executiveSynthesis) return;

  if (executiveSynthesis.leadWith !== 'business_implication_and_recommendation') {
    errors.push('executiveSynthesis.leadWith must be business_implication_and_recommendation.');
  }
  if (
    executiveSynthesis.sourceDetailMode !== 'drill_down_by_default' &&
    executiveSynthesis.sourceDetailMode !== 'top_level_when_recommendation_changes'
  ) {
    errors.push(
      'executiveSynthesis.sourceDetailMode must keep evidence detail behind drill-down unless it changes the recommendation.'
    );
  }
  if (executiveSynthesis.roleTailoringAllowed !== 'emphasis_only_truth_unchanged') {
    errors.push('executiveSynthesis.roleTailoringAllowed must be emphasis_only_truth_unchanged.');
  }
  if (executiveSynthesis.clientLanguageSource !== 'client_profile') {
    errors.push('executiveSynthesis.clientLanguageSource must be client_profile.');
  }
  if (!hasItems(executiveSynthesis.requiredFields)) {
    errors.push('executiveSynthesis.requiredFields must include executive-facing fields.');
  }

  const requiredFields: DecisionAgentExecutiveOutputField[] = [
    'business_implication',
    'recommendation',
    'why_this_matters',
    'options_tradeoffs',
    'evidence_assumptions',
    'control_plan',
  ];
  const fields = new Set(executiveSynthesis.requiredFields ?? []);
  requiredFields.forEach((field) => {
    if (!fields.has(field)) {
      errors.push(`executiveSynthesis.requiredFields must include ${field}.`);
    }
  });

  const forbiddenTerms = new Set(
    (executiveSynthesis.forbiddenVisibleTerms ?? []).map((term) => term.toLowerCase())
  );
  DEFAULT_DECISION_AGENT_EXECUTIVE_FORBIDDEN_VISIBLE_TERMS.forEach((term) => {
    if (!forbiddenTerms.has(term.toLowerCase())) {
      errors.push(`executiveSynthesis.forbiddenVisibleTerms must include ${term}.`);
    }
  });
}

function validateAskHelixBridgePolicy(
  manifest: DecisionAgentModuleManifest,
  errors: string[]
): void {
  const policy = manifest.askHelixBridge;
  if (!policy) return;

  const scenarioExecution = policy.scenarioExecution;
  if (scenarioExecution) {
    if (
      scenarioExecution.mode !== 'always' &&
      scenarioExecution.mode !== 'never' &&
      scenarioExecution.mode !== 'intent_driven'
    ) {
      errors.push(
        'askHelixBridge.scenarioExecution.mode must be always, never, or intent_driven.'
      );
    }
    if (scenarioExecution.mode === 'intent_driven') {
      requireStringArray(
        scenarioExecution.intentIds,
        'askHelixBridge.scenarioExecution.intentIds',
        errors,
        1
      );
    }
  }

  const optIn = policy.scenarioOptIn;
  if (optIn) {
    requireStringArray(
      optIn.requiredForIntentIds,
      'askHelixBridge.scenarioOptIn.requiredForIntentIds',
      errors,
      1
    );
    if (optIn.questionTerms) {
      requireStringArray(
        optIn.questionTerms,
        'askHelixBridge.scenarioOptIn.questionTerms',
        errors,
        1
      );
    }
    requireNonBlank(
      optIn.explicitOptInArgument,
      'askHelixBridge.scenarioOptIn.explicitOptInArgument',
      errors
    );
    requireNonBlank(optIn.message, 'askHelixBridge.scenarioOptIn.message', errors);
    requireStringArray(
      optIn.missingKeys,
      'askHelixBridge.scenarioOptIn.missingKeys',
      errors,
      1
    );
  }

  const directMetricAnswer = policy.directMetricAnswer;
  if (!directMetricAnswer) return;

  // #4008 lane DF1 — a matcher that can never resolve must not advertise.
  // The direct-metric renderer resolves its value exclusively from the scenario
  // payload (`renderDirectMetricAnswer` -> `readScenarioPayload` ->
  // `findScenarioMetric`). A module that never runs scenarios therefore declares a
  // policy that can never fire, while the separate `answerIntent` predicate keeps
  // advertising `direct_answer`. Fail the manifest instead of shipping the
  // contradiction.
  if (scenarioExecution?.mode === 'never') {
    errors.push(
      'askHelixBridge.directMetricAnswer cannot resolve when askHelixBridge.scenarioExecution.mode is never — the direct-metric renderer reads only the scenario payload.'
    );
  }

  if (!hasItems(directMetricAnswer.intentIds) && !hasItems(directMetricAnswer.questionTerms)) {
    errors.push(
      'askHelixBridge.directMetricAnswer must include intentIds or questionTerms.'
    );
  }
  if (directMetricAnswer.intentIds) {
    requireStringArray(
      directMetricAnswer.intentIds,
      'askHelixBridge.directMetricAnswer.intentIds',
      errors,
      1
    );
  }
  if (directMetricAnswer.questionTerms) {
    requireStringArray(
      directMetricAnswer.questionTerms,
      'askHelixBridge.directMetricAnswer.questionTerms',
      errors,
      1
    );
  }
  requireStringArray(
    directMetricAnswer.metricKeys,
    'askHelixBridge.directMetricAnswer.metricKeys',
    errors,
    1
  );

  const decisionMetricKeys = new Set(
    manifest.decisionPack.requiredMetrics.map((metric) => metric.metricKey)
  );
  directMetricAnswer.metricKeys?.forEach((metricKey) => {
    if (!decisionMetricKeys.has(metricKey)) {
      errors.push(
        `askHelixBridge.directMetricAnswer.metricKeys references unknown Decision Pack metric ${metricKey}.`
      );
    }
  });
}

function validateLedgerBinding(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  requireNonBlank(manifest.ledgerBinding.decisionType, 'ledgerBinding.decisionType', errors);
  requireNonBlank(manifest.ledgerBinding.scopeKind, 'ledgerBinding.scopeKind', errors);
  requireNonBlank(manifest.ledgerBinding.scopeId, 'ledgerBinding.scopeId', errors);
  requireNonBlank(manifest.ledgerBinding.ownerRole, 'ledgerBinding.ownerRole', errors);
  // #5631 — activationRequiresApproval retired (RIF/WR no-approvals law).
  const metricKeys = new Set(
    manifest.decisionPack.requiredMetrics.map((metric) => metric.metricKey)
  );
  if (
    !manifest.decisionPack.sourcePacketOnly &&
    !hasItems(manifest.ledgerBinding.verificationMetricKeys)
  ) {
    errors.push('ledgerBinding.verificationMetricKeys must include at least one metric key.');
  }
  manifest.ledgerBinding.verificationMetricKeys.forEach((metricKey) => {
    if (!metricKeys.has(metricKey)) {
      errors.push(`ledgerBinding.verificationMetricKeys references unknown metric ${metricKey}.`);
    }
  });
}

function validateMonitoring(manifest: DecisionAgentModuleManifest, errors: string[]): void {
  const monitoring = manifest.monitoring;
  if (!hasItems(monitoring.ruleIds)) {
    errors.push('monitoring.ruleIds must include at least one deterministic monitoring rule.');
  }
  if (monitoring.outputMetrics !== undefined && !manifest.decisionPack.sourcePacketOnly) {
    if (!hasItems(monitoring.outputMetrics)) {
      errors.push('monitoring.outputMetrics must include at least one monitoring output when present.');
    }
    monitoring.outputMetrics?.forEach((metricKey, index) => {
      if (isBlank(metricKey)) {
        errors.push(`monitoring.outputMetrics[${index}] must be a non-empty string.`);
      }
    });
  }
  if (!hasItems(monitoring.warningLevels)) {
    errors.push('monitoring.warningLevels must include at least one warning level.');
  }
  if (!monitoring.failClosedWhenSignalsMissing) {
    errors.push('monitoring.failClosedWhenSignalsMissing must be true.');
  }
}

function validateStatus(
  status: DecisionAgentModuleStatus | undefined,
  errors: string[]
): void {
  const allowed: DecisionAgentModuleStatus[] = [
    'demo',
    'shadow',
    'governed',
    'active',
    'retired',
  ];
  if (!status || !allowed.includes(status)) {
    errors.push(
      `status must be one of ${allowed.join(', ')}.`
    );
  }
}

function hasBoardGradeNumericMetadata(
  input: Partial<DecisionAgentNumericMetadataContract>
): boolean {
  return (
    !isBlank(input.definition) &&
    !isBlank(input.unit) &&
    !isBlank(input.scope) &&
    !isBlank(input.timeframe) &&
    !isBlank(input.source) &&
    !isBlank(input.asOf) &&
    !isBlank(input.reconciliationRule)
  );
}

function captureContractAssertion(assertion: () => unknown, errors: string[]): void {
  try {
    assertion();
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }
}

function requireNonBlank(value: string | undefined, fieldName: string, errors: string[]): void {
  if (isBlank(value)) {
    errors.push(`${fieldName} is required.`);
  }
}

function requireStringArray(
  values: readonly string[] | undefined,
  fieldName: string,
  errors: string[],
  minItems: number
): void {
  if (!Array.isArray(values)) {
    errors.push(`${fieldName} must be an array.`);
    return;
  }
  if (values.length < minItems) {
    errors.push(`${fieldName} must include at least ${minItems} item.`);
  }
  values.forEach((value, index) => {
    if (isBlank(value)) {
      errors.push(`${fieldName}[${index}] must be a non-empty string.`);
    }
  });
}

function hasItems<T>(items: readonly T[] | undefined): items is readonly T[] {
  return Array.isArray(items) && items.length > 0;
}

function isBlank(value: string | undefined): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}
